import { useEffect } from 'react';

/**
 * "The pane layout has settled" — a split, close, zoom, preset or the end of
 * a divider drag. Each terminal refits once more and sends its final size to
 * the program straight away (no drag debounce).
 *
 * Why it exists (owner, 24.09.2026): after a split the EXISTING pane sometimes
 * kept its old grid — the last row sat under the next pane's header — until
 * the divider was nudged. A drag works because the ResizeObserver sees many
 * small changes and the last refit lands on the final size; a split is one
 * jump, and a single refit can land before the pane has fully settled. This
 * pass runs after the change has stopped: two animation frames (layout done)
 * and once more a little later (late header/fonts), and only then.
 */
export const TERMINAL_LAYOUT_SETTLED_EVENT = 'apiboo:terminal-layout-settled';
export const SETTLE_LATE_MS = 150;

export function dispatchLayoutSettled() {
  window.dispatchEvent(new CustomEvent(TERMINAL_LAYOUT_SETTLED_EVENT));
}

/** Dispatch the settle event after `deps` stop changing (re-runs cancel the previous pass). */
export function useLayoutSettle(deps, enabled = true) {
  useEffect(() => {
    if (!enabled) return undefined;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(dispatchLayoutSettled);
    });
    const late = setTimeout(dispatchLayoutSettled, SETTLE_LATE_MS);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      clearTimeout(late);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);
}
